import { gql } from '@apollo/client'

// Seller / mall-owner management operations. Kept separate from the
// shopper-facing operations.ts. Hand-written gql + TS response interfaces,
// matching the project convention (no codegen). The backend re-checks role on
// every mutation; the UI gating is cosmetic.

// ---- Phase 0: current user (capability flags + mall id) -------------------

export const GET_CURRENT_USER = gql`
  query ManageCurrentUser {
    currentUser {
      id
      email
      fullName
      role
      canSell
      canManageMall
      mall { id name }
      store { id name }
    }
  }
`

export interface ManageCurrentUser {
  currentUser: {
    id: string
    email: string
    fullName: string
    role: string
    canSell: boolean
    canManageMall: boolean
    mall: { id: string; name: string } | null
    store: { id: string; name: string } | null
  } | null
}

// ---- Phase 1: My Store + store settings -----------------------------------

export const GET_MY_STORE = gql`
  query ManageMyStore {
    myStore {
      id
      name
      description
      status
      settings
      logoUrl
      bannerUrl
      productsCount
      activeProductsCount
      commissionEarned
      storeProducts {
        id
        isActive
        product { id title imageUrls status }
      }
    }
  }
`

export interface ManagedStoreSummary {
  id: string
  name: string
  description: string | null
  status: string
  settings: Record<string, unknown> | null
  logoUrl: string | null
  bannerUrl: string | null
  productsCount: number
  activeProductsCount: number
  commissionEarned: number | null
  storeProducts: {
    id: string
    isActive: boolean
    product: { id: string; title: string; imageUrls: string[] | null; status: string }
  }[]
}

export interface ManageMyStoreData {
  myStore: ManagedStoreSummary | null
}

export const UPDATE_STORE = gql`
  mutation ManageUpdateStore($id: ID!, $name: String, $description: String, $settings: JSON) {
    updateStore(id: $id, name: $name, description: $description, settings: $settings) {
      store { id name description status settings }
      userErrors { field message }
    }
  }
`

// ---- Phase 2: mall-owner store management ----------------------------------

const STORE_PERMS = `canEdit canDelete canApprove canPause canSuspend canClose canActivate`

export const GET_STORES = gql`
  query ManageStores($status: String) {
    stores(status: $status) {
      id name description status settings
      productsCount activeProductsCount
      user { id email fullName }
      ${STORE_PERMS}
    }
  }
`

export interface ManagedStore {
  id: string
  name: string
  description: string | null
  status: string
  settings: Record<string, unknown> | null
  productsCount: number
  activeProductsCount: number
  user: { id: string; email: string; fullName: string }
  canEdit: boolean; canDelete: boolean; canApprove: boolean
  canPause: boolean; canSuspend: boolean; canClose: boolean; canActivate: boolean
}

export interface ManageStoresData { stores: ManagedStore[] }

export const APPROVE_STORE = gql`
  mutation ManageApproveStore($id: ID!) {
    approveStore(id: $id) {
      store { id name status canEdit canApprove canPause canSuspend canClose }
      userErrors { field message }
    }
  }
`

export const CHANGE_STORE_STATUS = gql`
  mutation ManageChangeStoreStatus($id: ID!, $status: String!) {
    changeStoreStatus(id: $id, status: $status) {
      store { id name status canEdit canApprove canPause canSuspend canClose }
      userErrors { field message }
    }
  }
`

export const CREATE_STORE = gql`
  mutation ManageCreateStore($name: String!, $description: String, $settings: JSON) {
    createStore(name: $name, description: $description, settings: $settings) {
      store { id name status }
      userErrors { field message }
    }
  }
`

export const GET_STORE_BASIC = gql`
  query ManageStoreBasic($id: ID!) {
    store(id: $id) { id name description status settings }
  }
`

export interface ManageStoreBasicData {
  store: { id: string; name: string; description: string | null; status: string; settings: Record<string, unknown> | null } | null
}

// ---- Phase 3: mall-owner product management --------------------------------

export interface ManagedVariant {
  id: string; title: string; price: number; compareAtPrice: number | null
  sku: string | null; inventoryQuantity: number
  position?: number; option1?: string | null; option2?: string | null; option3?: string | null
  imageUrls?: string[] | null
}
export interface ManagedProductNode {
  id: string; title: string; handle: string; vendor: string | null
  productType: string | null; status: string; currency: string
  imageUrls: string[] | null; productVariants: ManagedVariant[]
}

export const GET_PRODUCTS_MANAGE = gql`
  query ManageProducts($first: Int, $after: String, $search: String, $status: String) {
    products(first: $first, after: $after, search: $search, status: $status) {
      edges {
        cursor
        node {
          id title handle vendor productType status currency imageUrls
          productVariants { id title price compareAtPrice sku inventoryQuantity }
        }
      }
      pageInfo { hasNextPage endCursor }
    }
  }
`
export interface ManageProductsData {
  products: {
    edges: { cursor: string; node: ManagedProductNode }[]
    pageInfo: { hasNextPage: boolean; endCursor: string | null }
  }
}

export const GET_PRODUCT_WITH_VARIANTS = gql`
  query ManageProductWithVariants($id: ID!) {
    product(id: $id) {
      id title handle description vendor productType tags status imageUrls
      productVariants { id title price compareAtPrice sku inventoryQuantity position option1 option2 option3 imageUrls }
    }
  }
`
export interface ManageProductWithVariants {
  // `tags` is the backend's comma-joined String field (the array form is the
  // separate `tagsList` field, not queried here).
  product: (ManagedProductNode & { description: string | null; tags: string | null }) | null
}

export const CREATE_PRODUCT = gql`
  mutation ManageCreateProduct(
    $mallId: ID!, $title: String!, $price: Float!, $description: String, $vendor: String,
    $productType: String, $tags: [String!], $status: String, $sku: String,
    $inventoryQuantity: Int, $compareAtPrice: Float,
    $option1Name: String, $option1Values: String, $option2Name: String, $option2Values: String
  ) {
    createProduct(
      mallId: $mallId, title: $title, price: $price, description: $description, vendor: $vendor,
      productType: $productType, tags: $tags, status: $status, sku: $sku,
      inventoryQuantity: $inventoryQuantity, compareAtPrice: $compareAtPrice,
      option1Name: $option1Name, option1Values: $option1Values, option2Name: $option2Name, option2Values: $option2Values
    ) {
      product { id title }
      userErrors { field message }
    }
  }
`

export const PRODUCT_UPDATE = gql`
  mutation ManageProductUpdate(
    $id: ID!, $title: String, $description: String, $vendor: String, $productType: String, $tags: String
  ) {
    productUpdate(id: $id, title: $title, description: $description, vendor: $vendor, productType: $productType, tags: $tags) {
      product { id title }
      userErrors { field message }
    }
  }
`

export const CREATE_PRODUCT_VARIANT = gql`
  mutation ManageCreateVariant(
    $productId: ID!, $title: String!, $price: Float!, $compareAtPrice: Float,
    $sku: String, $inventoryQuantity: Int, $option1: String, $option2: String, $option3: String
  ) {
    createProductVariant(
      productId: $productId, title: $title, price: $price, compareAtPrice: $compareAtPrice,
      sku: $sku, inventoryQuantity: $inventoryQuantity, option1: $option1, option2: $option2, option3: $option3
    ) { productVariant { id } userErrors { field message } }
  }
`

export const UPDATE_PRODUCT_VARIANT = gql`
  mutation ManageUpdateVariant(
    $id: ID!, $title: String, $price: Float, $compareAtPrice: Float,
    $sku: String, $inventoryQuantity: Int, $option1: String, $option2: String, $option3: String
  ) {
    updateProductVariant(
      id: $id, title: $title, price: $price, compareAtPrice: $compareAtPrice,
      sku: $sku, inventoryQuantity: $inventoryQuantity, option1: $option1, option2: $option2, option3: $option3
    ) { productVariant { id } userErrors { field message } }
  }
`

export const DELETE_PRODUCT_VARIANT = gql`
  mutation ManageDeleteVariant($id: ID!) {
    deleteProductVariant(id: $id) { success userErrors { field message } }
  }
`

export const UPLOAD_PRODUCT_MEDIA_BASE64 = gql`
  mutation ManageUploadProductMedia($productId: ID!, $images: [String!]) {
    uploadProductMediaBase64(productId: $productId, images: $images) {
      product { id imageUrls }
      userErrors { field message }
    }
  }
`

export const UPLOAD_PRODUCT_VARIANT_MEDIA_BASE64 = gql`
  mutation ManageUploadVariantMedia($variantId: ID!, $images: [String!]) {
    uploadProductVariantMediaBase64(variantId: $variantId, images: $images) {
      productVariant { id imageUrls }
      userErrors { field message }
    }
  }
`

export const STORE_PRODUCT_ADD = gql`
  mutation ManageStoreProductAdd($storeId: ID!, $productId: ID!, $commissionRate: Float!, $featured: Boolean) {
    storeProductAdd(storeId: $storeId, productId: $productId, commissionRate: $commissionRate, featured: $featured) {
      storeProduct { id }
      userErrors { field message }
    }
  }
`

export const STORE_PRODUCT_REMOVE = gql`
  mutation ManageStoreProductRemove($storeId: ID!, $productId: ID!) {
    storeProductRemove(storeId: $storeId, productId: $productId) {
      removedProductId
      userErrors { field message }
    }
  }
`

export const GET_PRODUCT_ASSIGNMENTS = gql`
  query ManageProductAssignments($id: ID!) {
    product(id: $id) {
      id title
      storeProducts { id commissionRate isActive store { id name } }
    }
  }
`
export interface ManageProductAssignmentsData {
  product: {
    id: string; title: string
    // storeProducts includes soft-deleted (isActive: false) rows — the backend
    // retains them for history. Filter to isActive when treating as "current".
    storeProducts: { id: string; commissionRate: number; isActive: boolean; store: { id: string; name: string } }[]
  } | null
}

// ---- Phase 4: mall settings -----------------------------------------------

export const GET_MALL_SETTINGS = gql`
  query ManageMallSettings($mallId: ID!) {
    mallById(id: $mallId) { id name description settings }
  }
`
export interface ManageMallSettingsData {
  mallById: { id: string; name: string; description: string | null; settings: Record<string, unknown> | null } | null
}

export const UPDATE_MALL_SETTINGS = gql`
  mutation ManageUpdateMallSettings(
    $mallId: ID!, $name: String, $description: String, $defaultCurrency: String,
    $livestreamingEnabled: Boolean, $chatEnabled: Boolean, $analyticsEnabled: Boolean
  ) {
    updateMallSettings(
      mallId: $mallId, name: $name, description: $description, defaultCurrency: $defaultCurrency,
      livestreamingEnabled: $livestreamingEnabled, chatEnabled: $chatEnabled, analyticsEnabled: $analyticsEnabled
    ) {
      mall { id name description settings }
      userErrors { field message }
    }
  }
`
